import React, { useMemo } from "react";
import { Routes, Route, useLocation, useParams } from "react-router-dom";
import { Box } from "@mui/material";
import { useAuth } from "../auth/AuthContext";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LocalTaxiIcon from "@mui/icons-material/LocalTaxi";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import CampaignIcon from "@mui/icons-material/Campaign";
import ExploreIcon from "@mui/icons-material/Explore";
import SettingsIcon from "@mui/icons-material/Settings";
import AppSettingsAltIcon from "@mui/icons-material/AppSettingsAlt";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import TuneIcon from "@mui/icons-material/Tune";
import MapIcon from "@mui/icons-material/Map";
import InsightsIcon from "@mui/icons-material/Insights";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";

import Home from "../pages/Home";
import Analytics from "../pages/Analytics";
import UserList from "../pages/UserList";
import DriverList from "../pages/DriverList";
import StaffAdminPage from "../pages/StaffAdminPage";
import LoginPage from "../pages/LoginPage";
import PrivateRoute from "../components/PrivateRoute";
import { logout } from "../services/authService";
import { isAuthenticated } from "../utils/auth";
import RideList from "../pages/RideList";
import RichNavDrawer from "../components/RichNavDrawer";
import AppConfigList from "../pages/AppConfigList";
import RideOptionList from "../pages/RideOptionList";
import RateCards from "../pages/RateCards";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import MarketingList from "../pages/MarketingList";
import TabbedSectionAdmin from "../pages/TabbedSectionAdmin";
import WalletList from "../pages/WalletList";
import SupportTicketList from "../pages/SupportTicketList";
import BotAnswers from "../pages/BotAnswers";
import BotPlayground from "../pages/BotPlayground";
import SupportAnalytics from "../pages/SupportAnalytics";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ScienceIcon from "@mui/icons-material/Science";
import SupportTicketDetail from "../pages/SupportTicketDetail";
import RideDetail from "../pages/RideDetail";
import UserDetail from "../pages/UserDetail";
import DriverDetail from "../pages/DriverDetail";
import NotificationComposer from "../pages/NotificationComposer";
import SubscriptionPlans from "../pages/SubscriptionPlans";
import BusinessSettings from "../pages/BusinessSettings";
import ZoneExplorer from "../pages/ZoneExplorer";
import ZoneDetails from "../pages/ZoneDetails";
import CityOperations from "../pages/CityOperations";
import AuditLogList from "../pages/AuditLogList";
import HistoryIcon from "@mui/icons-material/History";
import FinanceOps from "../pages/FinanceOps";
import LiveOps from "../pages/LiveOps";
import PaymentsIcon from "@mui/icons-material/Payments";
import SensorsIcon from "@mui/icons-material/Sensors";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import DriverPayouts from "../pages/DriverPayouts";
import PromoList from "../pages/PromoList";
import ReportCenter from "../pages/ReportCenter";
import QrInsights from "../pages/QrInsights";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AssessmentIcon from "@mui/icons-material/Assessment";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import SafetySos from "../pages/SafetySos";
import SupportPayables from "../pages/SupportPayables";
import SupportTeam from "../pages/SupportTeam";
import SupportSettings from "../pages/SupportSettings";
import PaidIcon from "@mui/icons-material/Paid";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import GroupsIcon from "@mui/icons-material/Groups";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import SafetyAlertBar from "../components/feedback/SafetyAlertBar";
import FraudReview from "../pages/FraudReview";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";

// R2 D2: a fresh detail per ticket id, so a revealed phone / canned replies / drafts never carry to the next ticket.
const KeyedTicketDetail: React.FC = () => { const { id } = useParams(); return <SupportTicketDetail key={id} />; };

const drawerWidth = 235;

// `permission` (optional) gates visibility; items without one are always shown.
// ANY-OF semantics when an array is provided.
// Owner 21 Oct: 22 top-level entries grouped into 9. Every page keeps its route and tick; a group with one visible page shows flat.
const menuItems = [
    { text: "Dashboard", icon: DashboardIcon, path: "/", color: "#24b9d4" },
    // R2b-4 (owner, 21 Oct): every customer-support page under one roof.
    {
        text: "Customer Support",
        icon: HeadsetMicIcon,
        color: "#24b9d4",
        children: [
            { text: "Tickets", icon: SupportAgentIcon, path: "/support-tickets", color: "#24b9d4", permission: "support:read", activeFor: ["/support-ticket/"] },
            { text: "Safety / SOS", icon: ReportProblemIcon, path: "/safety-sos", color: "#ef4444", permission: "sos:respond" },
            { text: "Compensation to pay", icon: PaidIcon, path: "/support-payables", color: "#f59e0b", permission: ["wallet:adjust", "support:lead"], isNew: true },
            { text: "Team & quality", icon: GroupsIcon, path: "/support-team", color: "#24b9d4", permission: "support:read", isNew: true },
            { text: "Support settings", icon: SettingsSuggestIcon, path: "/support-settings", color: "#24b9d4", permission: ["support:lead", "config:write"], isNew: true },
            { text: "Support Analytics", icon: InsightsIcon, path: "/support-analytics", color: "#24b9d4", permission: "support:read" },
            { text: "Bot Answers", icon: SmartToyIcon, path: "/support-bot-answers", color: "#8b5cf6", permission: "support:read" },
            { text: "Bot Playground", icon: ScienceIcon, path: "/support-bot-playground", color: "#8b5cf6", permission: "support:read" },
        ],
    },
    {
        text: "Rides & Operations",
        icon: LocalTaxiIcon,
        color: "#10B981",
        children: [
            { text: "Live Ops", icon: SensorsIcon, path: "/live-ops", color: "#10B981", permission: "rides:read", isNew: true },
            { text: "Rides", icon: LocalTaxiIcon, path: "/rides", color: "#24b9d4", permission: "rides:read", activeFor: ["/ride/"] },
            { text: "Fraud Review", icon: GppMaybeIcon, path: "/fraud-review", color: "#ef4444", permission: "rides:read", isNew: true },
            { text: "City Operations", icon: BusinessCenterIcon, path: "/city-operations", color: "#f59e0b", permission: "ops:bulk" },
        ],
    },
    {
        text: "People",
        icon: PeopleIcon,
        color: "#24b9d4",
        children: [
            { text: "Users", icon: PeopleIcon, path: "/users", color: "#24b9d4", permission: "users:read", activeFor: ["/user/"] },
            { text: "Drivers", icon: PersonIcon, path: "/drivers", color: "#24b9d4", permission: "drivers:read", activeFor: ["/driver/"] },
            { text: "Staff & Admins", icon: AdminPanelSettingsIcon, path: "/staff", color: "#ef4444", permission: "staff:read" },
        ],
    },
    {
        text: "Money",
        icon: PaymentsIcon,
        color: "#f59e0b",
        children: [
            { text: "Finance", icon: PaymentsIcon, path: "/finance", color: "#f59e0b", permission: ["wallet:read", "payments:read"], isNew: true },
            { text: "Driver Payouts", icon: AccountBalanceWalletIcon, path: "/driver-payouts", color: "#10B981", permission: "drivers:read", isNew: true },
        ],
    },
    {
        text: "Pricing & Zones",
        icon: MapIcon,
        color: "#f59e0b",
        children: [
            { text: "Ride Option", icon: DriveEtaIcon, path: "/rides-options", color: "#f59e0b", permission: "pricing:read" },
            { text: "Rate Cards", icon: ReceiptLongIcon, path: "/rate-cards", color: "#f59e0b", permission: "pricing:read", isNew: true },
            { text: "Zones", icon: MapIcon, path: "/zones", color: "#f59e0b", permission: "zones:read" },
        ],
    },
    {
        text: "Analytics & Reports",
        icon: InsightsIcon,
        color: "#10B981",
        children: [
            { text: "Analytics", icon: InsightsIcon, path: "/analytics", color: "#10B981", permission: "analytics:read" },
            { text: "Reports", icon: AssessmentIcon, path: "/reports", color: "#10B981", permission: ["rides:read", "payments:read", "wallet:read", "audit:read"], isNew: true },
            { text: "QR Insights", icon: QrCode2Icon, path: "/qr-insights", color: "#10B981", permission: "ops:bulk", isNew: true },
        ],
    },
    {
        text: "Marketing",
        icon: CampaignIcon,
        color: "#8b5cf6",
        children: [
            { text: "Push Notifications", icon: NotificationsActiveIcon, path: "/notifications/send", color: "#8b5cf6", permission: "notifications:send" },
            { text: "Promo Codes", icon: LocalOfferIcon, path: "/promos", color: "#8b5cf6", permission: "promos:read", isNew: true },
            { text: "Marketing", icon: CampaignIcon, path: "/marketing", color: "#8b5cf6", permission: "content:read" },
            { text: "Explore Items", icon: ExploreIcon, path: "/explore-list", color: "#8b5cf6", permission: "content:read" },
        ],
    },
    {
        text: "Settings",
        icon: SettingsIcon,
        color: "#94a3b8",
        children: [
            { text: "App Config", icon: AppSettingsAltIcon, path: "/app-config", color: "#94a3b8", permission: "config:read" },
            { text: "Revenue Plans", icon: MonetizationOnIcon, path: "/subscription-plans", color: "#f59e0b", permission: "subscriptions:read" },
            { text: "Business Logic", icon: TuneIcon, path: "/business-settings", color: "#94a3b8", permission: "subscriptions:read" },
            { text: "Audit Log", icon: HistoryIcon, path: "/audit-logs", color: "#94a3b8", permission: "audit:read", isNew: true },
            { text: "System Health", icon: TuneIcon, path: "/queues", isExternal: true, color: "#ef4444", permission: "config:write" },
        ],
    },
];


interface AppRoutesProps {
    mobileNavOpen?: boolean;
    onMobileNavClose?: () => void;
}

const AppRoutes: React.FC<AppRoutesProps> = ({ mobileNavOpen = false, onMobileNavClose }) => {
    const location = useLocation();
    const { hasAnyPermission, loading } = useAuth();
    const showDrawer = isAuthenticated() && location.pathname !== "/login";

    // Filter nav by the staff member's effective permissions. Parent groups are
    // shown only when at least one child is visible (one visible child shows flat). While /admin/me loads, show
    // the full menu to avoid a flash of empty nav.
    const visibleMenuItems = useMemo(() => {
        const canSee = (perm?: string | string[]) => {
            if (!perm || loading) return true;
            return hasAnyPermission((Array.isArray(perm) ? perm : [perm]) as any);
        };
        return menuItems
            .map((item: any) => {
                if (item.children) {
                    const children = item.children.filter((c: any) => canSee(c.permission));
                    if (children.length === 1) return children[0]; // no one-item folders
                    return children.length ? { ...item, children } : null;
                }
                return canSee(item.permission) ? item : null;
            })
            .filter(Boolean);
    }, [hasAnyPermission, loading]);

    return (
        <Box sx={{ display: "flex" }}>
            {showDrawer && (
                <RichNavDrawer
                    menuItems={visibleMenuItems as any}
                    onLogout={logout}
                    drawerWidth={drawerWidth}
                    mobileOpen={mobileNavOpen}
                    onMobileClose={onMobileNavClose}
                />
            )}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    px: { xs: 1, sm: 3, md: 4 },
                    py: { xs: 2, sm: 3 },
                    minHeight: '100vh',
                    overflowX: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    bgcolor: "#f5f7fa"
                }}
            >
                {showDrawer && <SafetyAlertBar />}
                <Routes>
                    <Route path="/login" element={<LoginPage />} />

                    {/* Core Routes */}
                    <Route element={<PrivateRoute><Home /></PrivateRoute>} path="/" />
                    <Route element={<PrivateRoute permission="analytics:read"><Analytics /></PrivateRoute>} path="/analytics" />
                    <Route element={<PrivateRoute permission="users:read"><UserList /></PrivateRoute>} path="/users" />
                    <Route element={<PrivateRoute permission="users:read"><UserDetail /></PrivateRoute>} path="/user/:id" />
                    <Route element={<PrivateRoute permission="drivers:read"><DriverList /></PrivateRoute>} path="/drivers" />
                    <Route element={<PrivateRoute permission="drivers:read"><DriverDetail /></PrivateRoute>} path="/driver/:id" />
                    <Route element={<PrivateRoute permission="staff:read"><StaffAdminPage /></PrivateRoute>} path="/staff" />
                    <Route element={<PrivateRoute permission="ops:bulk"><CityOperations /></PrivateRoute>} path="/city-operations" />
                    <Route element={<PrivateRoute permission="rides:read"><LiveOps /></PrivateRoute>} path="/live-ops" />
                    <Route element={<PrivateRoute permission={["wallet:read", "payments:read"]}><FinanceOps /></PrivateRoute>} path="/finance" />
                    <Route element={<PrivateRoute permission="drivers:read"><DriverPayouts /></PrivateRoute>} path="/driver-payouts" />
                    <Route element={<PrivateRoute permission="rides:read"><RideList /></PrivateRoute>} path="/rides" />
                    <Route element={<PrivateRoute permission="rides:read"><RideDetail /></PrivateRoute>} path="/ride/:id" />
                    <Route element={<PrivateRoute permission="rides:read"><FraudReview /></PrivateRoute>} path="/fraud-review" />
                    <Route element={<PrivateRoute permission="wallet:read"><WalletList /></PrivateRoute>} path="/wallet-transactions" />

                    {/* Management Routes */}
                    <Route element={<PrivateRoute permission="pricing:read"><RideOptionList /></PrivateRoute>} path="/rides-options" />
                    <Route element={<PrivateRoute permission="pricing:read"><RateCards /></PrivateRoute>} path="/rate-cards" />
                    <Route element={<PrivateRoute permission="zones:read"><ZoneExplorer /></PrivateRoute>} path="/zones" />
                    <Route element={<PrivateRoute permission="zones:read"><ZoneDetails /></PrivateRoute>} path="/zones/:id" />
                    <Route element={<PrivateRoute permission="content:read"><MarketingList /></PrivateRoute>} path="/marketing" />
                    <Route element={<PrivateRoute permission="content:read"><TabbedSectionAdmin /></PrivateRoute>} path="/explore-list" />
                    <Route element={<PrivateRoute permission="support:read"><SupportTicketList /></PrivateRoute>} path="/support-tickets" />
                    <Route element={<PrivateRoute permission="support:read"><BotAnswers /></PrivateRoute>} path="/support-bot-answers" />
                    <Route element={<PrivateRoute permission="support:read"><BotPlayground /></PrivateRoute>} path="/support-bot-playground" />
                    <Route element={<PrivateRoute permission="support:read"><SupportAnalytics /></PrivateRoute>} path="/support-analytics" />
                    <Route element={<PrivateRoute permission="support:read"><KeyedTicketDetail /></PrivateRoute>} path="/support-ticket/:id" />
                    <Route element={<PrivateRoute permission="sos:respond"><SafetySos /></PrivateRoute>} path="/safety-sos" />
                    <Route element={<PrivateRoute permission={["wallet:adjust", "support:lead"]}><SupportPayables /></PrivateRoute>} path="/support-payables" />
                    <Route element={<PrivateRoute permission="support:read"><SupportTeam /></PrivateRoute>} path="/support-team" />
                    <Route element={<PrivateRoute permission={["support:lead", "config:write"]}><SupportSettings /></PrivateRoute>} path="/support-settings" />
                    <Route element={<PrivateRoute permission="notifications:send"><NotificationComposer /></PrivateRoute>} path="/notifications/send" />
                    <Route element={<PrivateRoute permission="promos:read"><PromoList /></PrivateRoute>} path="/promos" />
                    <Route element={<PrivateRoute permission={["rides:read", "payments:read", "wallet:read", "audit:read"]}><ReportCenter /></PrivateRoute>} path="/reports" />
                    <Route element={<PrivateRoute permission="ops:bulk"><QrInsights /></PrivateRoute>} path="/qr-insights" />

                    {/* Settings Routes */}
                    <Route element={<PrivateRoute permission="config:read"><AppConfigList /></PrivateRoute>} path="/app-config" />
                    <Route element={<PrivateRoute permission="subscriptions:read"><SubscriptionPlans /></PrivateRoute>} path="/subscription-plans" />
                    <Route element={<PrivateRoute permission="subscriptions:read"><BusinessSettings /></PrivateRoute>} path="/business-settings" />
                    <Route element={<PrivateRoute permission="audit:read"><AuditLogList /></PrivateRoute>} path="/audit-logs" />
                </Routes>
            </Box>
        </Box>
    );
};

export default AppRoutes;